import { useCheckProtectedResources } from '@/components/InvoiceReportDetailDrawer/hooks/useCheckProtectedResources';
import { usePictureInPictureModeContext } from '@/context/InvoiceReports';
import { useBulkDeleteAdminInvoiceReportService } from '@/context/services/admin_invoice_reports/AdminInvoiceReportBulkDestroy.service';
import {
  isAuthError,
  isBadRequestError,
  isInvalidError,
  isNotFoundError,
  isPaymentRequiredError,
} from '@/context/services/type';
import { invalidateAdminInvoiceReportDetailQueries } from '@/features/admin_invoice_reports/components/AdminInvoiceReportDetailDrawer/utils/invalidateDetailQueries';
import { useTriggerFindTargetReport } from '@/features/admin_invoice_reports/components/List/context/TriggerFindTargetReportContext';
import { useTranslation } from '@/i18n';
import { extractErrorMessage } from '@/utils/extractErrorMessage';
import { toHtmlNode } from '@/utils/toHtmlNode';
import { useToastAPI } from '@moneyforward/ap-frontend-components';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

type UseBulkDeleteAdminInvoiceReportProps = {
  refetch: () => void | Promise<unknown>;
  onCloseBulkDeleteModal: () => void;
  selectedId?: string | null;
  onDrawerClose?: () => void;
};

export const useBulkDeleteAdminInvoiceReport = ({
  refetch,
  onCloseBulkDeleteModal,
  selectedId,
  onDrawerClose,
}: UseBulkDeleteAdminInvoiceReportProps) => {
  const notify = useToastAPI();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { onTriggerBackToPageOneIfNoData } = useTriggerFindTargetReport();
  const { mutateAsync: bulkDeleteAdminInvoiceReport } =
    useBulkDeleteAdminInvoiceReportService();
  const { handleCheckPermission } = useCheckProtectedResources();
  const { isPIPMode, openPictureInPicture } = usePictureInPictureModeContext();

  const notifyDeleteError = useCallback(
    (message: string) => {
      notify.error({
        title: toHtmlNode(message),
        duration: 5,
      });
    },
    [notify]
  );

  const invalidateDrawerIfOpen = useCallback(
    (selectedId: string, reportIds: string[]) => {
      // Only reflect changes if opening drawer is in the deleted list to reduce performance impact
      if (selectedId && reportIds.includes(selectedId)) {
        invalidateAdminInvoiceReportDetailQueries(queryClient, selectedId);
      }
    },
    [queryClient]
  );

  const bulkDeleteReports = useCallback(
    (reportIds: string[]) => async () => {
      const result = await bulkDeleteAdminInvoiceReport(reportIds);

      if (result.status === 'success') {
        onCloseBulkDeleteModal();
        notify.success({
          title: t('admin_invoice_reports.bulk_delete_success', {
            count: reportIds.length,
          }),
          duration: 5,
        });
        if (selectedId && reportIds.includes(selectedId)) {
          if (isPIPMode) {
            await openPictureInPicture(false);
          }
          onDrawerClose?.();
        }
        await refetch?.();
        onTriggerBackToPageOneIfNoData();
        return;
      }

      const errors = result.errors;
      const statusCode = errors.statusCode;
      const errorMessage =
        extractErrorMessage(errors, t, 'admin_invoice_reports.error') ??
        t('admin_invoice_reports.bulk_delete_error');

      // 401 — session: reload so session middleware can redirect.
      if (isAuthError(statusCode)) {
        location.reload();
        return;
      }

      // 402 — payment required: refetch the list to reflect the changes
      if (isPaymentRequiredError(statusCode)) {
        await refetch();
        notifyDeleteError(errorMessage);
        if (selectedId && reportIds.includes(selectedId)) {
          onDrawerClose?.();
        }
        return;
      }

      // 400 / 422 — business-rule rejection: nothing changed server-side
      if (
        isInvalidError(statusCode) ||
        isBadRequestError(statusCode) ||
        isNotFoundError(statusCode)
      ) {
        await refetch();
        invalidateDrawerIfOpen(selectedId ?? '', reportIds);
        onTriggerBackToPageOneIfNoData();
        notifyDeleteError(errorMessage);
        return;
      }

      // 403 + other unknown errors — verify the user still has list access
      await handleCheckPermission({
        fnCheckPermission: refetch,
        onCheckSuccess: () => {
          invalidateDrawerIfOpen(selectedId ?? '', reportIds);
          onTriggerBackToPageOneIfNoData();
          notifyDeleteError(errorMessage);
        },
        onCheckFailure: () => {
          location.reload();
        },
      });
    },
    [
      bulkDeleteAdminInvoiceReport,
      handleCheckPermission,
      isPIPMode,
      notify,
      onCloseBulkDeleteModal,
      onDrawerClose,
      openPictureInPicture,
      onTriggerBackToPageOneIfNoData,
      refetch,
      selectedId,
      t,
      notifyDeleteError,
      invalidateDrawerIfOpen,
    ]
  );

  return {
    bulkDeleteReports,
  };
};
